/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Chip, Skeleton } from '../../components/console/ui';
import ChargesTable from '../../components/billing/ChargesTable';
import GroupedChargesTable from '../../components/billing/GroupedChargesTable';
import {
  DATE_RANGES,
  DateRangeKey,
  downloadTransactionsCSV,
  groupTransactions,
  inRange,
  money,
  PRODUCT_FILTERS,
  ProductKey,
  productOf,
  rangeBounds,
} from '../../components/billing/billingUtils';
import {
  TX_PAGE_SIZE,
  useBillingContext,
  useFilteredTransactions,
  useTransactionGroups,
} from '../../components/billing/useBilling';
import { walletService } from '../../services/api';
import type { Transaction, TransactionQuery } from '../../types';

type Filter = 'all' | Exclude<ProductKey, 'other'>;

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All products' },
  { value: 'quickpods', label: 'QuickPods' },
  { value: 'trainpods', label: 'TrainPods' },
  { value: 'inference', label: 'Inference' },
  { value: 'storage', label: 'Object storage' },
  { value: 'topup', label: 'Top-ups & refunds' },
];

const GROUP_PAGE_SIZE = 50;
const CSV_MAX_PAGES = 50; // 5,000 rows

/**
 * Filters (product chips, date range) and per-day/per-resource grouping run
 * server-side. Against an older backend (no /transactions/grouped, filters
 * ignored) it falls back to filtering and grouping the loaded page.
 */
const BillingTransactions: React.FC = () => {
  const { currency, exchangeRate } = useBillingContext();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>('all');
  const [range, setRange] = useState<DateRangeKey>('30d');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [grouped, setGrouped] = useState(true);
  const [exporting, setExporting] = useState(false);

  const bounds = useMemo(() => rangeBounds(range, custom), [range, custom]);
  const query: TransactionQuery = useMemo(
    () => ({ ...(filter === 'all' ? {} : PRODUCT_FILTERS[filter]), ...bounds }),
    [filter, bounds],
  );

  const groupsQ = useTransactionGroups({ ...query, page, page_size: GROUP_PAGE_SIZE, include_summary: true }, grouped);
  const serverGrouping = grouped && !!groupsQ.data;
  // Raw rows: the ungrouped view, or the fallback when grouping isn't supported.
  const rawQ = useFilteredTransactions({ ...query, page, page_size: TX_PAGE_SIZE }, !grouped || groupsQ.data === null);
  const legacy = !serverGrouping && !!rawQ.data?.legacy;

  const raw = useMemo(() => rawQ.data?.transactions || [], [rawQ.data]);
  // Legacy backends ignore the filters: apply them to the loaded page.
  const visible = useMemo(
    () =>
      legacy
        ? raw.filter((t) => (filter === 'all' || productOf(t) === filter) && inRange(t.created_at, bounds))
        : raw,
    [legacy, raw, filter, bounds],
  );
  const clientGroups = useMemo(() => groupTransactions(visible), [visible]);

  const active = serverGrouping ? groupsQ : rawQ;
  const isLoading = (grouped && groupsQ.isLoading) || (!serverGrouping && rawQ.isLoading);
  const isError = serverGrouping ? groupsQ.isError : rawQ.isError || (grouped && groupsQ.isError);
  const total = serverGrouping ? groupsQ.data?.total || 0 : rawQ.data?.total || 0;
  const pageSize = serverGrouping ? GROUP_PAGE_SIZE : TX_PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const summary = serverGrouping ? groupsQ.data?.summary : legacy ? undefined : rawQ.data?.summary;
  const rowCount = serverGrouping ? groupsQ.data?.groups.length || 0 : visible.length;

  const resetPage = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  const downloadCSV = async () => {
    if (legacy) {
      downloadTransactionsCSV(visible);
      return;
    }
    setExporting(true);
    try {
      const rows: Transaction[] = [];
      let totalRows = 0;
      for (let p = 1; p <= CSV_MAX_PAGES; p++) {
        const { data } = await walletService.getTransactionsFiltered({ ...query, page: p, page_size: 100 });
        rows.push(...(data.transactions || []));
        totalRows = data.total || 0;
        if (rows.length >= totalRows || !data.transactions?.length) break;
      }
      if (totalRows > rows.length) {
        toast(`Exported the latest ${rows.length.toLocaleString('en-IN')} of ${totalRows.toLocaleString('en-IN')} lines. Narrow the date range for the rest.`);
      }
      downloadTransactionsCSV(rows);
    } catch {
      toast.error('Couldn’t export transactions. Try again in a moment.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="flex gap-2 flex-wrap items-center">
        {FILTERS.map((f) => (
          <Chip key={f.value} on={filter === f.value} onClick={() => resetPage(setFilter)(f.value)}>
            {f.label}
          </Chip>
        ))}
        <span className="flex-1" />
        <label className="sr-only" htmlFor="tx-range">
          Date range
        </label>
        <select
          id="tx-range"
          className="ps-input !w-auto"
          value={range}
          onChange={(e) => resetPage(setRange)(e.target.value as DateRangeKey)}
        >
          {DATE_RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {range === 'custom' && (
          <>
            <label className="sr-only" htmlFor="tx-from">
              From
            </label>
            <input
              id="tx-from"
              type="date"
              className="ps-input !w-auto"
              value={custom.from}
              max={custom.to || undefined}
              onChange={(e) => resetPage(setCustom)({ ...custom, from: e.target.value })}
            />
            <span className="text-ps-subtle text-sm">to</span>
            <label className="sr-only" htmlFor="tx-to">
              To
            </label>
            <input
              id="tx-to"
              type="date"
              className="ps-input !w-auto"
              value={custom.to}
              min={custom.from || undefined}
              onChange={(e) => resetPage(setCustom)({ ...custom, to: e.target.value })}
            />
          </>
        )}
        <label className="flex gap-2 items-center text-sm min-h-[44px] text-ps-text cursor-pointer">
          <input
            type="checkbox"
            checked={grouped}
            onChange={(e) => resetPage(setGrouped)(e.target.checked)}
            className="w-[18px] h-[18px] accent-[#F97316]"
          />
          Group per-minute charges
        </label>
        <Button variant="secondary" onClick={downloadCSV} disabled={exporting || total === 0}>
          {exporting ? 'Exporting…' : 'Download CSV'}
        </Button>
      </div>

      {summary && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ps-muted" aria-live="polite">
          <span>
            <b className="text-ps-text font-medium">{summary.count.toLocaleString('en-IN')}</b> transactions
          </span>
          <span>
            Charges <b className="text-ps-text font-medium">{money(summary.debits, currency, exchangeRate)}</b>
          </span>
          <span>
            Credits <b className="text-ps-ok font-medium">{money(summary.credits, currency, exchangeRate)}</b>
          </span>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-72" />
      ) : isError ? (
        <div className="ps-card p-6 text-sm text-ps-muted">Couldn’t load transactions. Try again in a moment.</div>
      ) : rowCount === 0 ? (
        <div className="ps-card p-6 text-sm text-ps-muted">
          {legacy && raw.length > 0
            ? 'No transactions for this filter on this page.'
            : filter === 'all' && range === 'all'
              ? 'No transactions yet.'
              : 'No transactions match these filters.'}
        </div>
      ) : (
        (() => {
          const footer = (
            <div className={`flex justify-between gap-2.5 flex-wrap items-center ${active.isFetching ? 'opacity-60' : ''}`}>
              <span>
                {serverGrouping
                  ? `Grouped charges ${from.toLocaleString('en-IN')}–${to.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`
                  : `${grouped ? `${clientGroups.length} grouped rows from ` : ''}transactions ${from.toLocaleString(
                      'en-IN',
                    )}–${to.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}${legacy && (filter !== 'all' || range !== 'all') ? ' (filtered on this page)' : ''}`}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page <= 1 || active.isFetching}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= pages || active.isFetching}
                >
                  Next
                </Button>
              </div>
            </div>
          );
          return serverGrouping ? (
            <GroupedChargesTable groups={groupsQ.data?.groups || []} currency={currency} rate={exchangeRate} footer={footer} />
          ) : (
            <ChargesTable
              groups={grouped ? clientGroups : undefined}
              raw={grouped ? undefined : visible}
              currency={currency}
              rate={exchangeRate}
              footer={footer}
            />
          );
        })()
      )}
    </>
  );
};

export default BillingTransactions;
